import { useEffect, useMemo, useState } from 'react'
import {
  Gauge, Gear, ArrowsClockwise, Warning, Trash, FloppyDisk, CaretDown, CaretRight, Spinner, DownloadSimple,
  Lightning, Drop, Sun, Flame, ThermometerHot, Cloud, XCircle, CheckCircle, CurrencyEur,
} from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { runAllDetectors, PHASE_1_DETECTORS } from '../../lib/iroDetectors'
import FloodMap from './FloodMap'

const C = {
  text: '#2C2017', muted: '#7A6A5A', border: '#DDD5C8',
  olive: '#8D9A2E', amber: '#C98A2E', coral: '#D4614A',
  bg: '#FFFFFF', bgAlt: '#FAF7F3',
  impact: '#D4614A', risk: '#C98A2E', opportunity: '#8D9A2E',
}
const CARD = { background: C.bg, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: `1px solid ${C.border}` }
const FONT = "'DM Sans', sans-serif"

const TYPE_META = {
  impact:      { Icon: XCircle,     label: 'Impacto',     color: C.impact },
  risk:        { Icon: Warning,     label: 'Riesgo',      color: C.risk },
  opportunity: { Icon: CheckCircle, label: 'Oportunidad', color: C.opportunity },
}

const STATUS_LABEL = {
  detected:   'Detectado',
  assessed:   'Evaluado',
  mitigating: 'Mitigando',
  closed:     'Cerrado',
  dismissed:  'Descartado',
}

const DEFAULT_SETTINGS = {
  gross_area_m2: '',
  baseline_co2_kgm2_year: '',
  baseline_eui_kwh_m2_year: '',
  eua_price_eur_per_t: 85,
  gas_emission_factor: 0.2016,
  fv_export_tariff_eur_kwh: 0.06,
  // Taxonomía UE 7.7 — criterio literal (Reglamento Delegado UE 2021/2139, Anexo I, 7.7)
  construction_year: '',
  cee_class: '',                       // criterio (a) pre-2020: clase A o superior
  cee_class_source: '',
  cep_nren_designed_kwh_m2: '',        // criterio (b) post-2020: Cep,nren diseñado
  cep_nren_designed_source: '',
  nzeb_threshold_kwh_m2: '',           // criterio (b) post-2020: NZEB threshold (Cep,B-C)
  nzeb_threshold_source: '',
  has_pv: false,
  municipality: '',
  // D3 · Estrés hídrico regional (ESRS E3-4 + DNSH Taxonomía UE)
  // El asesor introduce el nivel oficial declarado por la autoridad hidrográfica
  building_typology: '',           // 'hotel' | 'residencial' | 'oficinas' | 'retail'
  water_stress_level: '',          // 'normalidad' | 'prealerta' | 'alerta' | 'excepcionalidad' | 'emergencia'
  water_stress_region: '',         // ej. 'Cuencas internas de Cataluña — sistema Ter-Llobregat'
  water_stress_source: '',         // ej. 'ACA · Butlletí Setmanal de Sequera'
  water_stress_source_date: '',    // fecha de consulta oficial
  notes: '',
}

function bandDots(n) {
  const filled = Math.max(0, Math.min(5, Number(n) || 0))
  return '●'.repeat(filled) + '○'.repeat(5 - filled)
}

function fmtEur(v) {
  if (v == null) return '—'
  const n = Number(v)
  if (!isFinite(n)) return '—'
  return `${Math.round(n).toLocaleString('es-ES')} €`
}

export default function OperationalIROs({ project, rows }) {
  const [settings, setSettings]   = useState(null)   // null = not loaded yet
  const [iros, setIros]           = useState([])
  const [loading, setLoading]     = useState(false)
  const [running, setRunning]     = useState(false)
  const [error, setError]         = useState(null)
  const [tableMissing, setTableMissing] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showFloodMap, setShowFloodMap] = useState(false)
  const [weather, setWeather] = useState(new Map())
  const [zbeInfo, setZbeInfo] = useState(null)
  const [draft, setDraft]         = useState(DEFAULT_SETTINGS)
  const [expanded, setExpanded]   = useState(new Set())
  const [importMsg, setImportMsg] = useState(null)   // {ok, text} feedback al importar del CEE
  const [importBasis, setImportBasis] = useState('eval')  // 'eval' (diseñado) | 'ref' (banda C)

  // ── Load settings + IROs ───────────────────────────────────────────────────
  const load = async () => {
    if (!project?.id) return
    setLoading(true); setError(null); setTableMissing(false)
    try {
      const s = await supabase
        .from('operational_iro_settings')
        .select('*')
        .eq('project_id', project.id)
        .maybeSingle()

      if (s.error) {
        // Postgres "relation does not exist" code is 42P01
        const code = s.error.code || ''
        const msg = s.error.message || ''
        if (code === '42P01' || /does not exist/i.test(msg)) {
          setTableMissing(true)
          setLoading(false)
          return
        }
        throw new Error(s.error.message)
      }
      const loaded = s.data || null
      setSettings(loaded)
      setDraft(loaded ? { ...DEFAULT_SETTINGS, ...loaded } : DEFAULT_SETTINGS)

      const i = await supabase
        .from('operational_iros')
        .select('*')
        .eq('project_id', project.id)
        .order('period_year', { ascending: false })
        .order('period_month', { ascending: false })

      if (i.error) {
        const code = i.error.code || ''
        if (code === '42P01') { setTableMissing(true); setLoading(false); return }
        throw new Error(i.error.message)
      }
      setIros(i.data || [])

      // Cargar datos meteorológicos (HDD/CDD) del proyecto — para D9 y D10
      const w = await supabase
        .from('weather_hdd_cdd')
        .select('year, month, hdd_15_5, cdd_22, avg_temp_c, station_code, station_name')
        .eq('project_id', project.id)
      if (!w.error && w.data) {
        const map = new Map()
        for (const row of w.data) {
          const key = `${row.year}-${String(row.month).padStart(2,'0')}`
          map.set(key, { hdd: Number(row.hdd_15_5) || 0, cdd: Number(row.cdd_22) || 0, avg_temp_c: Number(row.avg_temp_c) })
        }
        setWeather(map)
      }

      // Lookup ZBE por municipio del proyecto — alimenta D8
      const muniRaw = loaded?.municipality || ''
      const norm = muniRaw
        .toLowerCase()
        .trim()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')  // quita acentos
        .replace(/'/g, '')
      if (norm) {
        const z = await supabase
          .from('zbe_municipios')
          .select('name, zbe_status, zbe_name, zbe_start_date, source')
          .eq('name_normalized', norm)
          .maybeSingle()
        if (!z.error && z.data) {
          setZbeInfo({ status: z.data.zbe_status, name: z.data.name, zbe_name: z.data.zbe_name, zbe_start_date: z.data.zbe_start_date, source: z.data.source })
        } else {
          setZbeInfo({ status: 'none', name: muniRaw })
        }
      } else {
        setZbeInfo(null)
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // load depende solo de project?.id (ya en deps).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [project?.id])

  // ── Save settings ──────────────────────────────────────────────────────────
  const saveSettings = async () => {
    setError(null)
    if (!project?.id) { setError('Sin proyecto activo'); return }
    try {
      const { data: u } = await supabase.auth.getUser()
      const user_id = u?.user?.id
      if (!user_id) throw new Error('No autenticado')
      const payload = {
        project_id: project.id,
        user_id,
        gross_area_m2:             toNumOrNull(draft.gross_area_m2),
        baseline_co2_kgm2_year:    toNumOrNull(draft.baseline_co2_kgm2_year),
        baseline_eui_kwh_m2_year:  toNumOrNull(draft.baseline_eui_kwh_m2_year),
        eua_price_eur_per_t:       toNumOrNull(draft.eua_price_eur_per_t) ?? 85,
        gas_emission_factor:       toNumOrNull(draft.gas_emission_factor) ?? 0.2016,
        fv_export_tariff_eur_kwh:  toNumOrNull(draft.fv_export_tariff_eur_kwh) ?? 0.06,
        // Taxonomía UE 7.7 — criterio literal Reglamento Delegado UE 2021/2139
        construction_year:         toNumOrNull(draft.construction_year),
        cee_class:                 (draft.cee_class || '').toUpperCase() || null,
        cee_class_source:          draft.cee_class_source || null,
        cep_nren_designed_kwh_m2:  toNumOrNull(draft.cep_nren_designed_kwh_m2),
        cep_nren_designed_source:  draft.cep_nren_designed_source || null,
        nzeb_threshold_kwh_m2:     toNumOrNull(draft.nzeb_threshold_kwh_m2),
        nzeb_threshold_source:     draft.nzeb_threshold_source || null,
        has_pv:                    !!draft.has_pv,
        municipality:              (draft.municipality || '').trim() || null,
        // D3 · Estrés hídrico regional (ESRS E3-4)
        building_typology:         (draft.building_typology || '').trim() || null,
        water_stress_level:        (draft.water_stress_level || '').trim() || null,
        water_stress_region:       (draft.water_stress_region || '').trim() || null,
        water_stress_source:       (draft.water_stress_source || '').trim() || null,
        water_stress_source_date:  (draft.water_stress_source_date || '').trim() || null,
        notes:                     draft.notes || null,
      }
      const { error } = await supabase
        .from('operational_iro_settings')
        .upsert(payload, { onConflict: 'project_id' })
      if (error) throw new Error(error.message)
      setShowSettings(false)
      await load()
    } catch (e) {
      setError(e.message)
    }
  }

  // ── Run detectors ──────────────────────────────────────────────────────────
  const recompute = async () => {
    setError(null)
    if (!project?.id) { setError('Sin proyecto activo'); return }
    if (!settings)    { setError('Configura primero los parámetros del activo (icono ⚙️).'); return }
    if (!rows || rows.length === 0) { setError('Sin lecturas mensuales — añade datos primero.'); return }

    setRunning(true)
    try {
      const { data: u } = await supabase.auth.getUser()
      const user_id = u?.user?.id
      if (!user_id) throw new Error('No autenticado')

      const waterStressInfo = settings?.water_stress_level ? {
        level: settings.water_stress_level,
        region: settings.water_stress_region || null,
        source: settings.water_stress_source || null,
        source_date: settings.water_stress_source_date || null,
      } : null

      const detected = runAllDetectors(rows, settings, weather, zbeInfo, waterStressInfo)

      if (detected.length === 0) {
        // Nothing to upsert. Show feedback by reloading (table may have older entries
        // we leave as-is — manual cleanup via "Descartar" if needed).
        await load()
        return
      }

      const payloads = detected.map(d => ({
        project_id: project.id,
        user_id,
        ...d,
      }))

      const { error } = await supabase
        .from('operational_iros')
        .upsert(payloads, { onConflict: 'project_id,detector_id,period_year,period_month' })
      if (error) throw new Error(error.message)

      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setRunning(false)
    }
  }

  const updateStatus = async (id, status) => {
    setError(null)
    try {
      const { error } = await supabase
        .from('operational_iros')
        .update({ status })
        .eq('id', id)
      if (error) throw new Error(error.message)
      await load()
    } catch (e) {
      setError(e.message)
    }
  }

  const removeIro = async (id) => {
    if (!window.confirm('¿Descartar este IRO? (puedes recalcularlo cuando quieras)')) return
    setError(null)
    try {
      const { error } = await supabase.from('operational_iros').delete().eq('id', id)
      if (error) throw new Error(error.message)
      await load()
    } catch (e) {
      setError(e.message)
    }
  }

  // ── Import baseline from ENE 1 calculator (CEE) ────────────────────────────
  const importFromENE1 = async (basis = 'eval') => {
    setImportMsg(null)
    if (!project?.id) { setImportMsg({ ok: false, text: 'Sin proyecto activo' }); return }
    try {
      let zones = null

      // 1) Try DB: evaluations row for category='Energía', criteria_code='ENE 1'
      const { data: evRow, error: evErr } = await supabase
        .from('evaluations')
        .select('justificacion')
        .eq('project_id', project.id)
        .eq('category', 'Energía')
        .eq('criteria_code', 'ENE 1')
        .maybeSingle()
      if (!evErr && evRow?.justificacion) {
        try {
          const parsed = JSON.parse(evRow.justificacion)
          zones = parsed?.ene1_calculadora?.zonas || null
        } catch { /* fallback */ }
      }

      // 2) Fallback to localStorage
      if (!zones || !zones.length) {
        try {
          const raw = localStorage.getItem(`ene1_${project.id}`)
          if (raw) {
            const saved = JSON.parse(raw)
            zones = saved?.zones || null
          }
        } catch { /* ignore */ }
      }

      if (!zones || !zones.length) {
        setImportMsg({ ok: false, text: 'No se han encontrado datos de ENE 1 para este proyecto. Carga primero el Certificado de Eficiencia Energética en la calculadora ENE 1.' })
        return
      }

      const co2Key = basis === 'ref' ? 'co2_ref' : 'co2_eval'
      const conKey = basis === 'ref' ? 'con_ref' : 'con_eval'

      // Filter zones with valid m² and at least one of the baseline fields
      const valid = zones.filter(z => Number(z.m2) > 0 && (Number(z[co2Key]) > 0 || Number(z[conKey]) > 0))
      if (!valid.length) {
        setImportMsg({ ok: false, text: `Hay zonas en ENE 1 pero sin valores de ${basis === 'ref' ? 'referencia (banda C)' : 'edificio evaluado'} válidos.` })
        return
      }

      const totalM2 = valid.reduce((acc, z) => acc + Number(z.m2), 0)
      const wAvg = (key) => {
        let num = 0, den = 0
        for (const z of valid) {
          const v = Number(z[key]); const m = Number(z.m2)
          if (isFinite(v) && v > 0 && isFinite(m) && m > 0) { num += v * m; den += m }
        }
        return den > 0 ? num / den : null
      }

      const co2 = wAvg(co2Key)
      const eui = wAvg(conKey)

      setDraft(d => ({
        ...d,
        gross_area_m2: Math.round(totalM2 * 10) / 10,
        baseline_co2_kgm2_year: co2 != null ? +co2.toFixed(2) : d.baseline_co2_kgm2_year,
        baseline_eui_kwh_m2_year: eui != null ? +eui.toFixed(1) : d.baseline_eui_kwh_m2_year,
      }))

      const summary = [
        `${valid.length} zona${valid.length === 1 ? '' : 's'} de ENE 1`,
        `${totalM2.toLocaleString('es-ES')} m²`,
        co2 != null ? `CO₂ ${co2.toFixed(2)} kg/m²·año` : null,
        eui != null ? `EUI ${eui.toFixed(1)} kWh/m²·año` : null,
        `(base: ${basis === 'ref' ? 'banda C / referencia' : 'edificio evaluado / diseño'})`,
      ].filter(Boolean).join(' · ')
      setImportMsg({ ok: true, text: 'Importado de CEE → ' + summary + '. Revisa y pulsa Guardar para persistir.' })
    } catch (e) {
      setImportMsg({ ok: false, text: e.message })
    }
  }

  // ── Aggregations ───────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const c = { impact: 0, risk: 0, opportunity: 0, total: 0 }
    for (const r of iros) {
      if (r.status === 'dismissed' || r.status === 'closed') continue
      c.total += 1
      if (c[r.type] != null) c[r.type] += 1
    }
    return c
  }, [iros])

  const totalEur = useMemo(() => {
    return iros
      .filter(r => r.status !== 'dismissed' && r.status !== 'closed')
      .reduce((acc, r) => acc + (Number(r.financial_estimate_eur) || 0), 0)
  }, [iros])

  // ── Render ─────────────────────────────────────────────────────────────────
  if (!project?.id) return null

  return (
    <div style={{ ...CARD, fontFamily: FONT }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.875rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '0.95rem', color: C.text, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <Gauge size={16} /> IROs operativos detectados <span style={{ color: C.muted, fontWeight: 400, fontSize: '0.78rem' }}>· Fase 1 · rolling 12m</span>
          </h3>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.77rem', color: C.muted }}>
            Identificación automatizada de Impactos / Riesgos / Oportunidades a partir de las lecturas mensuales del edificio. Alimenta CSRD (matriz de doble materialidad) y GRESB (sección RM).
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setShowSettings(v => !v)}
            style={btnGhost()}>
            <Gear size={13} /> Parámetros
          </button>
          <button onClick={recompute} disabled={running || tableMissing}
            style={btnPrimary(running || tableMissing)}>
            {running ? <Spinner size={13} /> : <ArrowsClockwise size={13} />}
            {running ? 'Calculando…' : 'Recalcular IROs'}
          </button>
        </div>
      </div>

      {tableMissing && (
        <div style={alertBox('amber')}>
          <Warning size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          La tabla <code>operational_iros</code> aún no está creada en la base de datos.
          Aplica la migración <code>supabase/migrations/20260629_operational_iros.sql</code> en Supabase (SQL editor o CLI) y vuelve a entrar.
        </div>
      )}

      {error && (
        <div style={alertBox('coral')}>
          <Warning size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {error}
        </div>
      )}

      {/* Settings drawer */}
      {showSettings && !tableMissing && (
        <div style={{ background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0.875rem', marginBottom: '0.875rem' }}>
          <div style={{ fontSize: '0.78rem', color: C.muted, marginBottom: '0.6rem' }}>
            Parámetros de calibración del activo. Pulsa <strong>Importar del CEE</strong> para auto-rellenar m² y baselines desde la calculadora ENE 1, o introduce los valores manualmente.
          </div>

          {/* Importar del CEE (ENE 1) */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', padding: '0.5rem 0.6rem', background: '#FFF', border: `1px dashed ${C.border}`, borderRadius: 4 }}>
            <span style={{ fontSize: '0.75rem', color: C.muted }}>Base CEE:</span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: C.text }}>
              <input type="radio" name="importBasis" value="eval" checked={importBasis === 'eval'} onChange={() => setImportBasis('eval')} />
              Edificio evaluado (diseño)
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: C.text }}>
              <input type="radio" name="importBasis" value="ref" checked={importBasis === 'ref'} onChange={() => setImportBasis('ref')} />
              Edificio de referencia (banda C)
            </label>
            <button onClick={() => importFromENE1(importBasis)} style={{ ...btnGhost(), marginLeft: 'auto' }}>
              <DownloadSimple size={13} /> Importar del CEE (ENE 1)
            </button>
          </div>
          {importMsg && (
            <div style={alertBox(importMsg.ok ? 'amber' : 'coral')}>
              {importMsg.text}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.6rem' }}>
            {settingField('Superficie útil (m²)', 'gross_area_m2', draft, setDraft, { step: '1' })}
            {settingField('Baseline CO₂ CEE (kg/m²·año)', 'baseline_co2_kgm2_year', draft, setDraft, { step: '0.1' })}
            {settingField('Baseline EUI CEE (kWh/m²·año)', 'baseline_eui_kwh_m2_year', draft, setDraft, { step: '0.1' })}
            {settingField('Precio EUA proyectado (€/tCO₂)', 'eua_price_eur_per_t', draft, setDraft, { step: '1' })}
            {settingField('Factor emisión gas (kgCO₂/kWh)', 'gas_emission_factor', draft, setDraft, { step: '0.0001' })}
            {settingField('Tarifa export FV (€/kWh)', 'fv_export_tariff_eur_kwh', draft, setDraft, { step: '0.001' })}
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', color: C.text, padding: '1.3rem 0 0' }}>
              <input type="checkbox" checked={!!draft.has_pv}
                onChange={e => setDraft({ ...draft, has_pv: e.target.checked })} />
              El activo tiene fotovoltaica
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
              Municipio del activo <span style={{ color: '#B0A89E', fontSize: '0.66rem' }}>(para D8 — riesgo ZBE)</span>
              <input type="text" value={draft.municipality || ''}
                onChange={e => setDraft({ ...draft, municipality: e.target.value })}
                placeholder="Ej. Sant Cugat del Vallès"
                style={{ padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.84rem', background: '#FFF' }} />
            </label>
          </div>

          {/* Bloque Taxonomía UE 7.7 — criterio literal */}
          <div style={{ marginTop: '0.9rem', paddingTop: '0.7rem', borderTop: `1px dashed ${C.border}` }}>
            <div style={{ fontSize: '0.72rem', color: C.muted, marginBottom: '0.45rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
              Taxonomía UE — actividad 7.7
            </div>
            <div style={{ fontSize: '0.72rem', color: C.muted, marginBottom: '0.55rem', lineHeight: 1.45 }}>
              Criterio literal del <em>Reglamento Delegado (UE) 2021/2139, Anexo I, sección 7.7</em>. Aplica un sub-criterio según fecha de construcción:
            </div>
            <ul style={{ margin: '0 0 0.6rem 1.2rem', padding: 0, fontSize: '0.72rem', color: C.muted, lineHeight: 1.5 }}>
              <li><strong>(a) Pre-2020:</strong> el activo tiene CEE clase A o superior.</li>
              <li><strong>(b) Post-2020:</strong> cumple actividad 7.1 → Cep,nren ≤ NZEB × 0,9 (CTE-HE).</li>
            </ul>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.6rem' }}>
              {settingField('Año finalización obra', 'construction_year', draft, setDraft, { step: '1' })}
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
                Clase CEE <span style={{ color: '#B0A89E', fontSize: '0.66rem' }}>(criterio a)</span>
                <select value={draft.cee_class || ''}
                  onChange={e => setDraft({ ...draft, cee_class: e.target.value })}
                  style={{ padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.84rem', fontFamily: "'IBM Plex Mono', monospace", background: '#FFF' }}>
                  <option value="">— sin definir —</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                  <option value="E">E</option>
                  <option value="F">F</option>
                  <option value="G">G</option>
                </select>
              </label>
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted, marginTop: '0.55rem' }}>
              Cita literal del CEE <span style={{ color: '#B0A89E', fontSize: '0.66rem' }}>(criterio a — organismo emisor, fecha, código registro)</span>
              <textarea value={draft.cee_class_source || ''}
                onChange={e => setDraft({ ...draft, cee_class_source: e.target.value })}
                rows={2}
                placeholder='Ej. "CEE del Hotel QGAT (Ampliació), emitido por Generalitat de Catalunya, registro nº XXXX, fecha YYYY-MM-DD, escala global Cep,nren = clase A"'
                style={{ padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.78rem', fontFamily: FONT, background: '#FFF', resize: 'vertical' }} />
            </label>

            {/* Sub-bloque criterio (b) post-2020 */}
            <div style={{ marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: `1px dotted ${C.border}` }}>
              <div style={{ fontSize: '0.7rem', color: C.muted, marginBottom: '0.45rem', fontWeight: 500 }}>
                Sub-criterio (b) — sólo edificios construidos a partir de 2021:
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.6rem' }}>
                {settingField('Cep,nren diseñado (kWh/m²·año)', 'cep_nren_designed_kwh_m2', draft, setDraft, { step: '0.01' })}
                {settingField('Umbral NZEB / Cep,B-C (kWh/m²·año)', 'nzeb_threshold_kwh_m2', draft, setDraft, { step: '0.01' })}
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted, marginTop: '0.55rem' }}>
                Cita literal del Cep,nren diseñado <span style={{ color: '#B0A89E', fontSize: '0.66rem' }}>(documento, página, sección)</span>
                <textarea value={draft.cep_nren_designed_source || ''}
                  onChange={e => setDraft({ ...draft, cep_nren_designed_source: e.target.value })}
                  rows={2}
                  placeholder='Ej. "Verificación CTE-HE0 del proyecto Ampliació Hotel QGat, PROGETIC (HULC v1.0.1564.1124), fecha 22/10/2018, página 1, valor Cep = 73,19 kWh/m²·año"'
                  style={{ padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.78rem', fontFamily: FONT, background: '#FFF', resize: 'vertical' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted, marginTop: '0.4rem' }}>
                Cita literal del umbral NZEB / Cep,B-C <span style={{ color: '#B0A89E', fontSize: '0.66rem' }}>(documento, página, sección)</span>
                <textarea value={draft.nzeb_threshold_source || ''}
                  onChange={e => setDraft({ ...draft, nzeb_threshold_source: e.target.value })}
                  rows={2}
                  placeholder='Ej. "Mismo documento Verificación CTE-HE0, página 1, valor Cep,B-C = 168,00 kWh/m²·año = Valor máximo de consumo de energía primaria no renovable para la clase B"'
                  style={{ padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.78rem', fontFamily: FONT, background: '#FFF', resize: 'vertical' }} />
              </label>
            </div>
          </div>

          {/* Bloque D3 · Estrés hídrico regional */}
          <div style={{ marginTop: '0.9rem', paddingTop: '0.7rem', borderTop: `1px dashed ${C.border}` }}>
            <div style={{ fontSize: '0.72rem', color: C.muted, marginBottom: '0.45rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
              D3 · Estrés hídrico regional (ESRS E3-4 + DNSH Taxonomía UE)
            </div>
            <div style={{ fontSize: '0.72rem', color: C.muted, marginBottom: '0.55rem', lineHeight: 1.45 }}>
              Introduce el nivel oficial declarado por la autoridad hidrográfica competente (MITECO Boletín Hidrológico, ACA para Cataluña, CH · confederación local). El detector solo emite riesgo si el nivel es ≥ Alerta.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.6rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
                Tipología del edificio <span style={{ color: '#B0A89E', fontSize: '0.66rem' }}>(umbral GRESB Water)</span>
                <select value={draft.building_typology || ''}
                  onChange={e => setDraft({ ...draft, building_typology: e.target.value })}
                  style={{ padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.84rem', background: '#FFF' }}>
                  <option value="">— genérico —</option>
                  <option value="hotel">Hotel · 2,5 m³/m²·año</option>
                  <option value="residencial">Residencial · 1,5 m³/m²·año</option>
                  <option value="oficinas">Oficinas · 0,5 m³/m²·año</option>
                  <option value="retail">Retail · 0,8 m³/m²·año</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
                Nivel oficial de sequía <span style={{ color: '#B0A89E', fontSize: '0.66rem' }}>(Plan Especial de Sequías)</span>
                <select value={draft.water_stress_level || ''}
                  onChange={e => setDraft({ ...draft, water_stress_level: e.target.value })}
                  style={{ padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.84rem', background: '#FFF' }}>
                  <option value="">— sin declarar —</option>
                  <option value="normalidad">Normalidad</option>
                  <option value="prealerta">Prealerta</option>
                  <option value="alerta">Alerta</option>
                  <option value="excepcionalidad">Excepcionalidad</option>
                  <option value="emergencia">Emergencia</option>
                </select>
              </label>
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted, marginTop: '0.55rem' }}>
              Región / demarcación hidrográfica
              <input type="text" value={draft.water_stress_region || ''}
                onChange={e => setDraft({ ...draft, water_stress_region: e.target.value })}
                placeholder='Ej. "Cuencas internas de Cataluña · sistema Ter-Llobregat"'
                style={{ padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.84rem', background: '#FFF' }} />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.6rem', marginTop: '0.55rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
                Fuente oficial consultada
                <input type="text" value={draft.water_stress_source || ''}
                  onChange={e => setDraft({ ...draft, water_stress_source: e.target.value })}
                  placeholder='Ej. "ACA · Butlletí Setmanal de Sequera"'
                  style={{ padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.84rem', background: '#FFF' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
                Fecha de consulta / vigencia
                <input type="date" value={draft.water_stress_source_date || ''}
                  onChange={e => setDraft({ ...draft, water_stress_source_date: e.target.value })}
                  style={{ padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.84rem', background: '#FFF' }} />
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button onClick={() => { setShowSettings(false); setDraft(settings ? { ...DEFAULT_SETTINGS, ...settings } : DEFAULT_SETTINGS) }}
              style={btnGhost()}>Cancelar</button>
            <button onClick={saveSettings} style={btnPrimary()}>
              <FloppyDisk size={13} /> Guardar parámetros
            </button>
          </div>
        </div>
      )}

      {/* KPI badges */}
      {!tableMissing && (
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.875rem' }}>
          <Kpi color={C.impact}      Icon={XCircle}     label="Impactos"      value={counts.impact} />
          <Kpi color={C.risk}        Icon={Warning}     label="Riesgos"       value={counts.risk} />
          <Kpi color={C.opportunity} Icon={CheckCircle} label="Oportunidades" value={counts.opportunity} />
          <Kpi color={C.text}        Icon={CurrencyEur} label="Exposición/Beneficio" value={fmtEur(totalEur)} text />
        </div>
      )}

      {/* Banda informativa ARPSI (siempre visible si hay verificación) */}
      {!tableMissing && settings?.arpsi_status && settings.arpsi_status !== 'not_checked' && (
        <>
          <div style={{ background: settings.arpsi_status === 'not_at_risk' ? '#F0F5EC' : '#FFF6E5',
                        border: `1px solid ${settings.arpsi_status === 'not_at_risk' ? C.olive : C.amber}44`,
                        borderRadius: 6, padding: '0.55rem 0.8rem', marginBottom: showFloodMap ? '0.4rem' : '0.7rem',
                        display: 'flex', alignItems: 'center', gap: 10 }}>
            {settings.arpsi_status === 'not_at_risk'
              ? <CheckCircle size={16} weight="regular" style={{ color: C.olive, flexShrink: 0 }} />
              : <Warning size={16} weight="regular" style={{ color: C.amber, flexShrink: 0 }} />}
            <div style={{ fontSize: '0.75rem', color: C.text, lineHeight: 1.4, flex: 1 }}>
              <strong>Verificación ARPSI</strong> (MITECO SNCZI, Directiva 2007/60/CE) — {settings.arpsi_check_date && <span style={{ color: C.muted }}>consultado el {settings.arpsi_check_date}</span>}
              {' '}·{' '}
              {settings.arpsi_status === 'not_at_risk'
                ? <span>Activo <strong>NO clasificado</strong> en ninguna Área con Riesgo Potencial Significativo de Inundación.</span>
                : <span>Activo clasificado en ARPSI — riesgo periodo de retorno <strong>{settings.arpsi_status.replace('at_risk_T','T=')} años</strong>. Ver detalle del IRO D11.</span>}
            </div>
            {settings?.latitude != null && settings?.longitude != null && (
              <button onClick={() => setShowFloodMap(v => !v)}
                style={{ ...btnGhost(), flexShrink: 0, fontSize: '0.72rem' }}>
                {showFloodMap ? 'Ocultar mapa' : 'Ver mapa'}
              </button>
            )}
            <span style={{ fontSize: '0.68rem', color: C.muted, fontStyle: 'italic', flexShrink: 0 }}>
              El informe se genera desde <strong>Informes Técnicos</strong>
            </span>
          </div>
          {showFloodMap && settings?.latitude != null && settings?.longitude != null && (
            <div style={{ marginBottom: '0.7rem' }}>
              <FloodMap
                latitude={settings.latitude}
                longitude={settings.longitude}
                addressLabel={settings.address}
                height={360}
              />
              <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: 4, lineHeight: 1.4 }}>
                Base cartográfica: OpenStreetMap. Capas ARPSI: MITECO SNCZI (WMS oficial). Usa el control de la esquina superior derecha para activar/desactivar cada capa de riesgo (T=10 = alta probabilidad, T=500 = baja).
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty / loading states */}
      {!tableMissing && loading && (
        <div style={{ color: C.muted, fontSize: '0.82rem', padding: '0.5rem 0' }}>Cargando IROs…</div>
      )}
      {!tableMissing && !loading && iros.length === 0 && (
        <div style={{ color: C.muted, fontSize: '0.82rem', padding: '0.5rem 0', background: C.bgAlt, border: `1px dashed ${C.border}`, borderRadius: 6 }}>
          <div style={{ padding: '0.75rem' }}>
            Aún no se ha detectado ningún IRO operativo en este activo.<br />
            Configura los parámetros del edificio (Engranaje arriba a la derecha) y pulsa <strong>Recalcular IROs</strong> para analizar las {rows?.length || 0} lecturas cargadas.
          </div>
        </div>
      )}

      {/* IRO table */}
      {!tableMissing && iros.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
                <th style={th('center', '32px')}></th>
                <th style={th('left')}>Tipo</th>
                <th style={th('left')}>ESRS</th>
                <th style={th('left')}>Detector</th>
                <th style={th('center')}>Severidad</th>
                <th style={th('right')} title="Estimación financiera anual (sólo D7 EU ETS y D12 excedente FV la generan; los IROs cualitativos muestran «n/a»)">Impacto €/año</th>
                <th style={th('center')}>Año</th>
                <th style={th('center')}>Estado</th>
                <th style={th('center', '80px')}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {iros.map((r, i) => {
                const isOpen = expanded.has(r.id)
                const meta = TYPE_META[r.type] || TYPE_META.impact
                const det = PHASE_1_DETECTORS.find(d => d.id === r.detector_id)
                return (
                  <FragmentRow key={r.id}
                    bg={i % 2 === 0 ? C.bg : C.bgAlt}
                    isOpen={isOpen}
                    onToggle={() => {
                      setExpanded(prev => {
                        const next = new Set(prev)
                        if (next.has(r.id)) next.delete(r.id); else next.add(r.id)
                        return next
                      })
                    }}
                    iro={r}
                    meta={meta}
                    det={det}
                    onStatusChange={(s) => updateStatus(r.id, s)}
                    onRemove={() => removeIro(r.id)}
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Catálogo de detectores — panel de referencia siempre visible */}
      {!tableMissing && (
        <div style={{ marginTop: '1.1rem', border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ background: C.bgAlt, padding: '0.55rem 0.85rem', fontSize: '0.7rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>
            Catálogo de detectores — estado en este proyecto
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
            <thead>
              <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
                <th style={th('left', '55px')}>ID</th>
                <th style={th('left')}>Detector</th>
                <th style={th('left', '160px')}>Estado</th>
                <th style={th('left')}>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {PHASE_1_DETECTORS.map((d, i) => {
                const st = catalogStatus(d.id, iros, settings, rows)
                return (
                  <tr key={d.id} style={{ background: i % 2 === 0 ? C.bg : C.bgAlt, borderBottom: '1px solid #F0EBE2' }}>
                    <td style={td('left', null, { fontFamily: "'IBM Plex Mono', monospace", color: C.muted })}>{d.id.split('.')[0]}</td>
                    <td style={td('left')}>{d.label}</td>
                    <td style={td('left')}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: st.color, fontWeight: 500 }}>
                        <st.Icon size={13} weight="regular" /> {st.label}
                      </span>
                    </td>
                    <td style={td('left', null, { color: C.muted, fontSize: '0.72rem' })}>{st.note}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Determina el estado de un detector en el proyecto actual
function catalogStatus(detectorId, iros, settings, rows) {
  const active = (iros || []).find(i => i.detector_id === detectorId)
  if (active) {
    return {
      color: C.olive,
      Icon: CheckCircle,
      label: 'Detectado',
      note: `Severidad ${active.severity}/5 · estado "${active.status}"`,
    }
  }
  switch (detectorId) {
    case 'D1.intensidad_co2':
      if (!settings?.baseline_co2_kgm2_year) return { color: C.amber, Icon: Warning, label: 'Sin configurar', note: 'Falta baseline CO₂ en Parámetros' }
      return { color: C.muted, Icon: XCircle, label: 'No dispara', note: 'Emisiones dentro del baseline (sin exceso ≥10%)' }
    case 'D3.estres_hidrico': {
      const lvl = (settings?.water_stress_level || '').toLowerCase()
      if (!lvl) return { color: C.amber, Icon: Warning, label: 'Sin configurar', note: 'Falta nivel oficial de sequía (Boletín MITECO / ACA)' }
      if (lvl === 'normalidad' || lvl === 'prealerta') return { color: C.olive, Icon: CheckCircle, label: 'No aplica', note: `Zona en ${lvl} — sin riesgo hídrico declarado` }
      return { color: C.muted, Icon: XCircle, label: 'No dispara', note: 'Nivel oficial ≥ Alerta pero intensidad < umbral GRESB' }
    }
    case 'D7.eu_ets': {
      const hasGas = (rows || []).some(r => Number(r.gas_kwh) > 0)
      return { color: C.muted, Icon: XCircle, label: hasGas ? 'No dispara' : 'No aplica', note: hasGas ? 'Exposición estimada < 100 €/año' : 'Activo sin gas natural (100 % eléctrico)' }
    }
    case 'D8.riesgo_zbe':
      if (!settings?.municipality) return { color: C.amber, Icon: Warning, label: 'Sin configurar', note: 'Falta municipio del activo en Parámetros' }
      return { color: C.muted, Icon: XCircle, label: 'No aplica', note: `Municipio "${settings.municipality}" sin ZBE registrada` }
    case 'D9.obsolescencia_hvac':
      return { color: C.muted, Icon: XCircle, label: 'No dispara', note: 'Requiere ≥2 años completos de consumo + meteo (año/año)' }
    case 'D10.calor_cronico':
      return { color: C.muted, Icon: XCircle, label: 'No dispara', note: 'Requiere ≥6 años de datos AEMET (5 baseline + 1 actual)' }
    case 'D11.riesgo_inundacion':
      if (settings?.arpsi_status === 'not_at_risk') return { color: C.olive, Icon: CheckCircle, label: 'Verificado', note: 'No en zona ARPSI (ver banda superior)' }
      if (!settings?.arpsi_status || settings.arpsi_status === 'not_checked') return { color: C.amber, Icon: Warning, label: 'Pendiente', note: 'Sin comprobar contra WMS MITECO' }
      return { color: C.muted, Icon: XCircle, label: 'No dispara', note: 'Estado ARPSI desconocido' }
    case 'D12.excedente_fv':
      if (!settings?.has_pv) return { color: C.muted, Icon: XCircle, label: 'No aplica', note: 'Activo sin fotovoltaica declarada' }
      return { color: C.muted, Icon: XCircle, label: 'No dispara', note: 'Generación FV no supera consumo mensual' }
    case 'D13.taxonomia_7_7':
      if (!settings?.construction_year) return { color: C.amber, Icon: Warning, label: 'Sin configurar', note: 'Falta año de construcción en Parámetros' }
      if (settings.construction_year <= 2020 && (settings?.cee_class || '').toUpperCase() !== 'A')
        return { color: C.muted, Icon: XCircle, label: 'No cumple', note: `CEE clase ${settings.cee_class || '?'} < A (criterio a pre-2020)` }
      if (settings.construction_year > 2020 && (!settings?.cep_nren_designed_kwh_m2 || !settings?.nzeb_threshold_kwh_m2))
        return { color: C.amber, Icon: Warning, label: 'Sin configurar', note: 'Faltan Cep,nren y/o NZEB (criterio b post-2020)' }
      return { color: C.muted, Icon: XCircle, label: 'No cumple', note: 'Cep,nren real supera NZEB × 0,9' }
    case 'D14.performance_gap':
      if (!settings?.baseline_eui_kwh_m2_year) return { color: C.amber, Icon: Warning, label: 'Sin configurar', note: 'Falta baseline EUI del CEE en Parámetros' }
      return { color: C.muted, Icon: XCircle, label: 'No dispara', note: 'Consumo real dentro de tolerancia (<10 % vs CEE)' }
    default:
      return { color: C.muted, Icon: XCircle, label: '—', note: '' }
  }
}

// ── Sub-components ──────────────────────────────────────────────────────────

function FragmentRow({ bg, isOpen, onToggle, iro, meta, det, onStatusChange, onRemove }) {
  return (
    <>
      <tr style={{ background: bg, borderBottom: '1px solid #F0EBE2' }}>
        <td style={td('center')}>
          <button onClick={onToggle} style={iconBtn()}>
            {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
          </button>
        </td>
        <td style={td('left')}>
          <span title={meta.label} style={{ color: meta.color, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {meta.Icon && <meta.Icon size={14} weight="regular" />} {meta.label}
          </span>
        </td>
        <td style={td('left', null, { fontFamily: "'IBM Plex Mono', monospace" })}>{iro.esrs_datapoint || '—'}</td>
        <td style={td('left')}>{det?.label || iro.detector_id}</td>
        <td style={td('center', null, { fontFamily: "'IBM Plex Mono', monospace", color: meta.color })}>{bandDots(iro.severity)}</td>
        <td style={td('right', null, { fontFamily: "'IBM Plex Mono', monospace", color: iro.financial_estimate_eur == null ? '#B0A89E' : undefined })}
            title={iro.financial_estimate_eur == null ? 'No aplica: este tipo de IRO no genera estimación financiera directa' : undefined}>
          {iro.financial_estimate_eur == null ? 'n/a' : fmtEur(iro.financial_estimate_eur)}
        </td>
        <td style={td('center', null, { fontFamily: "'IBM Plex Mono', monospace", color: '#7A6A5A' })}>
          {iro.period_year}
        </td>
        <td style={td('center')}>
          <select value={iro.status} onChange={e => onStatusChange(e.target.value)}
            style={{ fontSize: '0.74rem', padding: '2px 4px', border: '1px solid #DDD5C8', borderRadius: 3, background: '#FFF' }}>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </td>
        <td style={td('center')}>
          <button onClick={onRemove} title="Descartar" style={iconBtn('#D4614A')}><Trash size={13} /></button>
        </td>
      </tr>
      {isOpen && (
        <tr style={{ background: bg }}>
          <td colSpan={9} style={{ padding: '0.5rem 1rem 0.875rem 2.4rem', borderBottom: '1px solid #F0EBE2' }}>
            <div style={{ fontSize: '0.74rem', color: '#7A6A5A', lineHeight: 1.55 }}>
              <DetectorOutputCard output={iro.detector_output} />
              {iro.literal_quote && (
                <>
                  <div style={{ fontSize: '0.68rem', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginTop: 10 }}>
                    Resumen citable (para informe)
                  </div>
                  <pre style={{ fontFamily: FONT, fontStyle: 'italic', color: '#2C2017', background: '#FAF7F3', border: `1px solid ${C.border}`, borderRadius: 4, padding: '0.5rem 0.7rem', marginTop: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.76rem' }}>{iro.literal_quote}</pre>
                </>
              )}
              {iro.evidence_rows?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong style={{ color: '#5C4A3A' }}>Evidencia:</strong> {iro.evidence_rows.length} lectura{iro.evidence_rows.length === 1 ? '' : 's'} de <code>building_sensors</code>.
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function Kpi({ color, Icon, label, value, text }) {
  return (
    <div style={{ background: '#FFF', border: `1px solid ${color}33`, borderLeft: `3px solid ${color}`, borderRadius: 6, padding: '0.5rem 0.75rem', minWidth: 140 }}>
      <div style={{ fontSize: '0.68rem', color: '#7A6A5A', letterSpacing: '0.04em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {Icon && <Icon size={12} weight="regular" style={{ color }} />} {label}
      </div>
      <div style={{ fontSize: text ? '0.95rem' : '1.25rem', fontWeight: 600, color, fontFamily: "'IBM Plex Mono', monospace" }}>{value}</div>
    </div>
  )
}

function settingField(label, key, draft, setDraft, opts = {}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: '#7A6A5A' }}>
      {label}
      <input type="number" step={opts.step || '0.01'} value={draft[key] ?? ''}
        onChange={e => setDraft({ ...draft, [key]: e.target.value })}
        style={{ padding: '0.4rem 0.55rem', border: `1px solid #DDD5C8`, borderRadius: 4, fontSize: '0.84rem', fontFamily: "'IBM Plex Mono', monospace", background: '#FFF' }} />
    </label>
  )
}

// ── Utilities ────────────────────────────────────────────────────────────────
function toNumOrNull(v) {
  if (v === '' || v == null) return null
  const n = Number(v)
  return isFinite(n) ? n : null
}

function btnGhost() {
  return { background: '#FFF', color: '#7A6A5A', border: '1px solid #DDD5C8', borderRadius: 5, padding: '6px 11px', fontSize: '0.74rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }
}
function btnPrimary(disabled) {
  return { background: disabled ? '#B0A89E' : '#8D9A2E', color: '#FFF', border: 'none', borderRadius: 5, padding: '6px 13px', fontSize: '0.76rem', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }
}
function alertBox(tone) {
  const map = { amber: { bg: '#FFF6E5', bd: '#C98A2E', fg: '#7A5318' }, coral: { bg: '#FFF1ED', bd: '#D4614A', fg: '#D4614A' } }
  const t = map[tone] || map.amber
  return { background: t.bg, border: `1px solid ${t.bd}`, color: t.fg, padding: '0.55rem 0.9rem', borderRadius: 6, fontSize: '0.78rem', marginBottom: '0.6rem' }
}
function th(align, width, extra = {}) {
  return { padding: '0.4rem 0.5rem', textAlign: align, ...(width ? { width } : {}), ...extra }
}
function td(align, width, extra = {}) {
  return { padding: '0.4rem 0.5rem', textAlign: align, ...(width ? { width } : {}), ...extra }
}
function iconBtn(color) {
  return { background: 'transparent', border: 'none', cursor: 'pointer', color: color || '#7A6A5A', padding: '3px 6px', display: 'inline-flex', alignItems: 'center' }
}


// ── Renderizado legible de detector_output (sustituye el JSON crudo) ──────────
const FIELD_META = {
  // Sección: Criterio normativo (aparece arriba, destacado)
  sub_criterion:              { section: 'crit', label: 'Sub-criterio aplicado', order: 1 },
  criterion:                  { section: 'crit', label: 'Cita normativa', order: 2, type: 'quote' },

  // Sección: Cálculo Taxonomía 7.7
  construction_year:          { section: 'calc', label: 'Año finalización obra', order: 10 },
  cee_class:                  { section: 'calc', label: 'Clase CEE del activo', order: 11 },
  cep_nren_designed_kwh_m2:   { section: 'calc', label: 'Cep,nren diseñado', order: 20, unit: 'kWh/m²·año' },
  nzeb_threshold_kwh_m2:      { section: 'calc', label: 'Umbral NZEB (Cep,B-C)', order: 21, unit: 'kWh/m²·año' },
  taxonomy_71_threshold_kwh_m2: { section: 'calc', label: 'Umbral Taxonomía 7.1 (NZEB × 0,9)', order: 22, unit: 'kWh/m²·año' },
  compliance_margin_pct:      { section: 'calc', label: 'Margen de cumplimiento', order: 23, unit: '%', highlight: true },

  // Sección: Cálculo Intensidad CO2 (D1)
  intensity_kg_m2:            { section: 'calc', label: 'Intensidad CO₂e actual', order: 30, unit: 'kg/m²·año' },
  baseline_kg_m2:             { section: 'calc', label: 'Baseline CO₂e (diseño)', order: 31, unit: 'kg/m²·año' },
  excess_pct:                 { section: 'calc', label: 'Exceso sobre baseline', order: 32, unit: '%', highlight: true },
  annual_co2_kg:              { section: 'calc', label: 'CO₂e anual', order: 33, unit: 'kg', format: 'number' },

  // Sección: Cálculo Performance Gap (D14)
  annual_final_kwh:           { section: 'calc', label: 'Consumo anual (energía final)', order: 34, unit: 'kWh', format: 'number' },
  annual_cep_nren_kwh:        { section: 'calc', label: 'Consumo anual (energía primaria no renovable)', order: 35, unit: 'kWh', format: 'number' },
  real_cep_nren_kwh_m2:       { section: 'calc', label: 'Cep,nren operacional real', order: 36, unit: 'kWh/m²·año', highlight: true },
  baseline_cep_nren_kwh_m2:   { section: 'calc', label: 'Cep,nren del CEE (referencia)', order: 37, unit: 'kWh/m²·año' },
  factor_source:              { section: 'src', label: 'Factores de paso (fuente normativa)', order: 65, type: 'quote' },

  // Sección: Cálculo EU ETS (D7)
  annual_gas_kwh:             { section: 'calc', label: 'Gas natural anual', order: 40, unit: 'kWh', format: 'number' },
  emission_factor:            { section: 'calc', label: 'Factor emisión gas', order: 41, unit: 'kgCO₂/kWh' },
  tCO2_year:                  { section: 'calc', label: 'Toneladas CO₂/año', order: 42, unit: 't' },
  eua_price:                  { section: 'calc', label: 'Precio EUA proyectado', order: 43, unit: '€/tCO₂' },

  // Sección: Cálculo FV (D12)
  annual_excess_kwh:          { section: 'calc', label: 'Excedente FV anual', order: 50, unit: 'kWh', format: 'number' },
  tariff_eur_kwh:             { section: 'calc', label: 'Tarifa exportación FV', order: 51, unit: '€/kWh' },

  // Sección: Fuentes documentales (citas literales del proyecto)
  cee_class_source:           { section: 'src', label: 'Cita literal — Clase CEE del activo', order: 60, type: 'quote' },
  cep_nren_designed_source:   { section: 'src', label: 'Cita literal — Cep,nren diseñado', order: 61, type: 'quote' },
  nzeb_threshold_source:      { section: 'src', label: 'Cita literal — Umbral NZEB', order: 62, type: 'quote' },

  // Sección: Datos operacionales
  operational_annual_kwh:     { section: 'ops', label: 'Consumo energético anual real', order: 70, unit: 'kWh', format: 'number' },
  operational_eui_kwh_m2:     { section: 'ops', label: 'EUI operacional real', order: 71, unit: 'kWh/m²·año' },
  months_covered:             { section: 'ops', label: 'Meses analizados', order: 72 },

  // Sección: Periodo (renderizador especial)
  period_summary:             { section: 'period', label: 'Resumen del periodo', order: 80, type: 'period' },

  // Sección: Nota
  note:                       { section: 'note', label: 'Nota', order: 99, type: 'note' },
}

const SECTION_LABELS = {
  crit:   'Criterio normativo aplicado',
  calc:   'Cálculo',
  src:    'Fuentes documentales (citas literales del proyecto)',
  ops:    'Datos operacionales (contexto)',
  period: 'Resumen del periodo',
  note:   null,
}

const KIND_ICON = { electricity: Lightning, water: Drop, gas: Flame, heat: ThermometerHot, fv: Sun }
const KIND_LABEL = { electricity: 'Electricidad de red', water: 'Agua potable', gas: 'Gas natural', heat: 'Calefacción urbana', fv: 'FV autoconsumida' }

function fmtNum(v, unit, format) {
  if (v == null || v === '') return '—'
  let s = v
  if (typeof v === 'number') s = format === 'number' ? v.toLocaleString('es-ES') : v.toLocaleString('es-ES', { maximumFractionDigits: 2 })
  return unit ? `${s} ${unit}` : String(s)
}

function DetectorOutputCard({ output }) {
  if (!output || typeof output !== 'object') return <span style={{ color: C.muted }}>Sin datos</span>

  // Agrupar campos por sección, respetando orden
  const bySection = {}
  Object.keys(output).forEach(k => {
    const meta = FIELD_META[k]
    if (!meta) return  // campos desconocidos: silenciosamente ignorados
    const section = meta.section
    if (!bySection[section]) bySection[section] = []
    bySection[section].push({ key: k, value: output[k], meta })
  })
  Object.values(bySection).forEach(arr => arr.sort((a, b) => a.meta.order - b.meta.order))

  const sectionOrder = ['crit', 'calc', 'src', 'ops', 'period', 'note']

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {sectionOrder.map(sec => {
        const items = bySection[sec]
        if (!items || items.length === 0) return null
        return (
          <div key={sec}>
            {SECTION_LABELS[sec] && (
              <div style={{ fontSize: '0.68rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 4 }}>
                {SECTION_LABELS[sec]}
              </div>
            )}
            {items.map(({ key, value, meta }) => {
              if (value == null || value === '') return null
              if (meta.type === 'quote') return <QuoteBox key={key} label={meta.label} text={value} />
              if (meta.type === 'note') return (
                <div key={key} style={{ fontSize: '0.72rem', color: C.muted, fontStyle: 'italic', marginTop: 4, borderTop: `1px dashed ${C.border}`, paddingTop: 6 }}>
                  {value}
                </div>
              )
              if (meta.type === 'period') return <PeriodSummaryCard key={key} summary={value} />
              // Campo simple (label + valor)
              return (
                <div key={key} style={{ display: 'flex', gap: 8, fontSize: '0.78rem', padding: '2px 0' }}>
                  <span style={{ color: C.muted, minWidth: 250 }}>{meta.label}:</span>
                  <span style={{ color: meta.highlight ? C.olive : C.text, fontWeight: meta.highlight ? 700 : 500, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {fmtNum(value, meta.unit, meta.format)}
                  </span>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

function QuoteBox({ label, text }) {
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontSize: '0.7rem', color: C.muted, marginBottom: 2, fontWeight: 500 }}>{label}</div>
      <div style={{ fontStyle: 'italic', fontSize: '0.76rem', color: C.text, padding: '5px 9px', borderLeft: `3px solid ${C.olive}`, background: '#FAF7F3', borderRadius: 2 }}>
        {text}
      </div>
    </div>
  )
}

function PeriodSummaryCard({ summary }) {
  if (!summary || typeof summary !== 'object') return null
  const { period, totals, invoices } = summary
  const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('es-ES'))
  return (
    <div style={{ background: '#FAF7F3', border: `1px solid ${C.border}`, borderRadius: 4, padding: '0.6rem 0.75rem', marginTop: 4 }}>
      {period && (
        <div style={{ fontSize: '0.72rem', color: C.muted, marginBottom: 6 }}>
          Del {period.start} al {period.end} · {period.months} {period.months === 1 ? 'mes' : 'meses'}
        </div>
      )}
      {totals && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '3px 12px', fontSize: '0.76rem', marginBottom: invoices?.length ? 8 : 0 }}>
          {totals.electricity_kwh > 0 && <TotalRow Icon={Lightning} label="Electricidad de red" value={`${fmt(totals.electricity_kwh)} kWh`} />}
          {totals.gas_kwh > 0 && <TotalRow Icon={Flame} label="Gas natural" value={`${fmt(totals.gas_kwh)} kWh`} />}
          {totals.district_heat_kwh > 0 && <TotalRow Icon={ThermometerHot} label="Calefacción urbana" value={`${fmt(totals.district_heat_kwh)} kWh`} />}
          {totals.energy_kwh_renewable > 0 && <TotalRow Icon={Sun} label="FV autoconsumida" value={`${fmt(totals.energy_kwh_renewable)} kWh`} />}
          {totals.water_m3_total > 0 && <TotalRow Icon={Drop} label="Agua potable" value={`${fmt(totals.water_m3_total)} m³`} />}
          {totals.waste_tons_total > 0 && <TotalRow Icon={Trash} label="Residuos" value={`${totals.waste_tons_total.toFixed(1)} t`} />}
          {(totals.scope1_kgco2e + totals.scope2_kgco2e) > 0 && <TotalRow Icon={Cloud} label="Scope 1+2" value={`${fmt(Math.round((totals.scope1_kgco2e + totals.scope2_kgco2e) / 1000))} tCO₂e`} />}
        </div>
      )}
      {invoices && invoices.length > 0 && (
        <>
          <div style={{ fontSize: '0.68rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500, marginBottom: 3, paddingTop: totals ? 6 : 0, borderTop: totals ? `1px dashed ${C.border}` : 'none' }}>
            Facturas analizadas ({invoices.reduce((s, i) => s + i.count, 0)})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {invoices.map((inv, i) => {
              const KindIco = KIND_ICON[inv.kind]
              return (
                <div key={i} style={{ fontSize: '0.76rem', color: C.text, display: 'flex', gap: 6, alignItems: 'center' }}>
                  {KindIco ? <KindIco size={13} weight="regular" style={{ color: C.muted, flexShrink: 0 }} /> : <span style={{ minWidth: 13 }} />}
                  <span>
                    <strong>{KIND_LABEL[inv.kind] || inv.kind}</strong>
                    {' · '}
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(inv.total_consumption)} {inv.unit}</span>
                    {' · '}
                    {inv.count} {inv.count === 1 ? 'factura' : 'facturas'}
                    {inv.total_eur > 0 && <> · <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(Math.round(inv.total_eur))} €</span></>}
                    {' · '}
                    <em style={{ color: C.muted }}>{inv.supplier || '(sin suministrador registrado)'}</em>
                  </span>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function TotalRow({ Icon, label, value }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {Icon ? <Icon size={13} weight="regular" style={{ color: C.muted, flexShrink: 0 }} /> : <span style={{ minWidth: 13 }} />}
      <span style={{ color: '#7A6A5A' }}>{label}:</span>
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#2C2017', fontWeight: 500 }}>{value}</span>
    </div>
  )
}
